import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, withStyles, useTheme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import IconButton from '@material-ui/core/IconButton';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import Paper from '@material-ui/core/Paper';
import TextField from "@material-ui/core/TextField";
import { isNotEmpty } from '../..//../../utils/Validator';
import { parseDate } from '../../../../utils/Utils';

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import PhysicianLocationDtlsTbl from "./PhysicianLocationDtlsTbl";
import Button from "@material-ui/core/Button";
import { Avatar, Typography, Box } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import Grid from '@material-ui/core/Grid';
import CheckboxSelection from '../CheckboxSelection';
import AddIcon from '@material-ui/icons/Add';
import UndoIcon from '@material-ui/icons/Undo';
import RestoreIcon from '@material-ui/icons/Restore';
import VisibilityIcon from '@material-ui/icons/Visibility';
import ProviderService from "../../../../Services/ProviderService";
import ConfirmationDialog from "../ConfirmationDialog";
import WarningIcon from '@material-ui/icons/Warning';


function desc(a, b, orderBy) {
  let c = a[orderBy];
  let d = b[orderBy];

  if (isNotEmpty(c)) {
    if (typeof c === "string") {
      c = c.toUpperCase();
    } else if (c instanceof Date) {
      c = parseDate(c);
    }
  } else {
    c = "";
  }

  if (isNotEmpty(d)) {
    if (typeof d === "string") {
      d = d.toUpperCase();
    } else if (d instanceof Date) {
      d = parseDate(d);
    }
  } else {
    d = "";
  }

  if (d < c) {
    return -1;
  }

  if (d > c) {
    return 1;
  }

  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

// Helper function to format Y/N to Yes/No for display
function formatYesNo(value) {
  if (value === 'Y') return 'YES';
  if (value === 'N') return 'NO';
  return value; // Return original value if not Y or N
}

// Helper function to extract focus areas from details and format as comma-separated string
// with ellipsis if there are more than 2 focus areas
function formatFocusAreas(details) {
  if (!details || !Array.isArray(details) || details.length === 0) {
    return '';
  }

  const focusAreas = details
    .map(detail => detail.focusShortDesc || '')
    .filter(Boolean);

  if (focusAreas.length <= 2) {
    return focusAreas.join(', ');
  } else {
    return `${focusAreas.slice(0, 2).join(', ')}...`;
  }
}

function formatFocusAreasComplete(details) {
  if (!details || !Array.isArray(details) || details.length === 0) {
    return '';
  }

  return details
    .map(detail => detail.focusShortDesc || '')
    .filter(Boolean)
    .join(', ');
}


// Helper function to format date for display
function formatDateForDisplay(date) {
  if (!date) return '';

  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch (error) {
    return '';
  }
}


const headCells = [
  { id: 'phoOfficeName', numeric: false, disablePadding: true, label: 'Name', filterLable: ' Name', width: '15%' },
  { id: 'address', numeric: false, disablePadding: true, label: 'Address', filterLable: ' Address', width: '20%' },
  { id: 'ofcDirectoryPhoneNbr', numeric: false, disablePadding: true, label: 'Phone', filterLable: 'Phone', width: '10%' },
  { id: 'ofcFaxNbr', numeric: false, disablePadding: true, label: 'Fax ', filterLable: 'Fax ', width: '10%' },
  { id: 'ofcEmailAddress', numeric: false, disablePadding: true, label: 'Email Address', filterLable: 'Email Address', width: '15%' },
  { id: 'ptcTaxId', numeric: false, disablePadding: true, label: 'Practice TIN', filterLable: 'Practice TIN', width: '10%' },
  { id: 'poflAcceptWcPatientsFlag', numeric: false, disablePadding: true, label: 'Accepting New WC?', filterLable: 'Accepting New WC?', width: '10%' },
  { id: 'FOCUS', numeric: false, disablePadding: true, label: 'Focus', filterLable: 'Focus', width: '10%' },
  { id: 'ACTIONS', numeric: false, disablePadding: true, label: 'Actions', filterLable: 'Actions', width: '10%' }
];

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort, onRequestFilter } = props;
  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };
  const onFilter = property => event => {
    onRequestFilter(event.target.value, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ width: headCell.width }}
            autoFocus={headCell.id === 'userFirstName'}
          >
            <TableSortLabel
              active={orderBy === headCell.id && headCell.id !== 'ACTIONS'}
              direction={order}
              onClick={headCell.id !== 'ACTIONS' ? createSortHandler(headCell.id) : null}
              disabled={headCell.id === 'ACTIONS'}
            >
              <div>
                <div>
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </span>) : null}
                </div>
              </div>
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
      <TableRow className='filter-row'>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id + 1}
            padding="none"
            style={{ maxWidth: headCell.width, padding: 0 }}
            autoFocus={headCell.id === 'userFirstName'}
          >
            {headCell.id !== 'ACTIONS' && (
              <div style={{ paddingRight: '0', verticalAlign: 'bottom' }}>
                <TextField
                  id={`filter-${headCell.id}`}
                  fullWidth
                  autoFocus={headCell.id === 'userFirstName'}
                  style={{ height: '.7', margin: 0 }}
                  placeholder="Filter..."
                  margin="none"
                  variant="outlined"
                  size="small"
                  onChange={onFilter(headCell.id)}
                  InputProps={{
                    style: { backgroundColor: 'transparent' }
                  }}
                />
              </div>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onRequestFilter: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = () => {
  return ({});
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles1 = makeStyles(theme => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

function TablePaginationActions(props) {
  const classes = useStyles1();
  const theme = useTheme();

  const { count, page, rowsPerPage, onChangePage } = props;

  const handleFirstPageButtonClick = event => {
    onChangePage(event, 0);
  };

  const handleBackButtonClick = event => {
    onChangePage(event, page - 1);
  };

  const handleNextButtonClick = event => {
    onChangePage(event, page + 1);
  };

  const handleLastPageButtonClick = event => {
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

const useStyles = theme => ({
  root: {
    width: '100%',
    marginTop: 0,
    maxWidth: '100%',
    padding: 0
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    maxWidth: '100%',
    padding: 0
  },
  table: {
    minWidth: 450,
    width: '100%',
  },
  tableWrapper: {
    overflowX: 'auto',
    width: '100%',
    maxWidth: '100%',
    padding: 0,
    margin: 0
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  deleted: {
    textDecoration: 'line-through',
    color: '#999',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: '100%',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
    borderTop: '1px solid #e0e0e0',
    marginTop: 0,
    marginBottom: 0
  },
  actionButton: {
    margin: theme.spacing(0, 1),
    minWidth: '160px', // Ensure buttons have consistent width
  },
});


class PhysicianLocationEdit extends Component {
  constructor(props) {
    super(props);
    this.dialogContentRef = React.createRef();
    this.state = {
      page: 0,
      rowsPerPage: 5,
      details: [],
      showDlg: false,
      showEditDlg: false,
      officeName: '',
      filtered: [],
      order: 'asc',
      orderBy: 'phoOfficeName',
      editLocation: null,
      deletedLocations: [],
      // Track changes
      pristineLocations: [],
      originalLocations: [], // Pristine original data
      workingLocations: [], // Current working copy with all pending changes applied
      changeTracking: [], // Array to track all changes with { action, originalObj, newObj, id }
      hasLocalChanges: false,
      formErrors: {},
      validationMessage: '',
      showConfirmationDialog: false,
      // New state for changes summary dialog
      showChangesSummaryDialog: false,
      changesSummary: {
        newLocations: [],
        modifiedLocations: [],
        deletedLocations: []
      },

      focusOptions: [],

      showDeleteConfirmDialog: false,
      locationToDelete: null,
      countyOptions: [],
      loadingCounties: false,
      expandedFocusRows: {},
      stateOptions: [],
      loadingStates: false,
      showAcceptingWcMessage: false,
    }
    this.handleToggleFocusExpansion = this.handleToggleFocusExpansion.bind(this);
  }

  componentDidMount() {
    const locationsDeepCopy = JSON.parse(JSON.stringify(this.props.locations || []));
    this.setState({
      pristineLocations: locationsDeepCopy,
      originalLocations: locationsDeepCopy,
      workingLocations: locationsDeepCopy
    });

    this.fetchFocusAreas();
    this.fetchStatesAndTerritories();

    // Expose methods to parent via ref
    if (this.props.forwardedRef) {
      this.props.forwardedRef.current = {
        validateForm: () => {

          return true;
        },
        getFormData: () => {
          const currentLocations = this.getCurrentLocations();

          // Generate a summary of changes for the parent
          const changesSummary = this.generateChangesSummary();

          return {
            pristineObject: this.state.pristineLocations,
            modifiedObject: this.state.hasLocalChanges ? {
              active: currentLocations,
              changes: changesSummary
            } : null,
            isValid: true
          };
        }
      };
    }
  }

  fetchFocusAreas = () => {
    //console.log('Fetching focus areas from service');
    ProviderService.getFocusAreasForProvider()
      .then(response => {
        if (response && response.data) {
          const focusOptions = response.data.map(focus => ({
            id: focus.focusId,
            value: focus.focusShortDesc, // Keep the short description as value
            acronym: focus.focusAcronym || '',
            // Format the display label with acronym and description
            label: `${focus.focusShortDesc || ''}`.trim()
          }));

          this.setState({ focusOptions });
        } else {
          console.error('No focus areas data returned from service');
        }
      })
      .catch(error => {
        console.error('Error fetching focus areas:', error);
      });
  }

  fetchStatesAndTerritories = () => {
    this.setState({ loadingStates: true });

    ProviderService.getStatesAndTerritories()
      .then(response => {
        if (response && response.data) {
          // Transform the data to include both code and name
          const stateOptions = response.data.map(record => ({
            value: record.srStateCode,  // Use the state code as value
            label: `${record.srStateName} (${record.srStateCode})`  // Format label with name and code
          }));

          this.setState({
            stateOptions: stateOptions,
            loadingStates: false
          });
        } else {
          console.error('No states data returned from service');
          this.setState({ loadingStates: false });
        }
      })
      .catch(error => {
        console.error('Error fetching states and territories:', error);
        this.setState({ loadingStates: false });
      });
  }

  componentDidUpdate(prevProps) {
    // Check if the locations prop has changed from parent
    if (prevProps.locations !== this.props.locations) {
      console.log('Locations prop changed from parent');

      // Only update original locations if we haven't made local changes
      // This prevents overwriting our tracked changes when parent updates
      if (!this.state.hasLocalChanges) {
        console.log('No local changes, updating original locations');
        const locationsDeepCopy = JSON.parse(JSON.stringify(this.props.locations || []));
        this.setState({
          originalLocations: locationsDeepCopy,
          workingLocations: locationsDeepCopy
        });
      } else {
        //console.log('Has local changes, not updating original locations');
      }

      // Always reset to first page when data changes
      this.setState({ page: 0 });
    }

    // Check if filtered locations have changed
    if (prevProps.filtered !== this.props.filtered) {
      //console.log('Filtered locations changed from parent');
      // Reset to first page when filter results change
      this.setState({ page: 0 });
    }
  }

  setFilter = (filDtls) => {
    this.setState({ filtered: filDtls });
  }

  closeMsgDlg = () => {
    this.setState({ details: [], showDlg: false });
  }

  closeEditDlg = () => {
    this.setState({
      editLocation: null,
      showEditDlg: false,
      formErrors: {},
      validationMessage: ''
    });
  }

  handleRequestSort = (event, property) => {
    const isDesc = this.state.orderBy === property && this.state.order === 'desc';
    this.setState({ order: isDesc ? 'asc' : 'desc', orderBy: property });
  }

  // Track changes to a location
  trackChange = (action, originalObj, newObj, id) => {
    console.log(`Tracking change - Action: ${action}, ID: ${id}`);
    console.log('Original Object:', originalObj);
    console.log('New Object:', newObj);

    this.setState(prevState => {
      // Create a copy of the current change tracking array
      let updatedChangeTracking = [...prevState.changeTracking];

      // Find if we already have a change for this ID
      const existingChangeIndex = updatedChangeTracking.findIndex(
        change => change.id === id
      );

      if (existingChangeIndex !== -1) {
        //console.log(`Updating existing change for ID: ${id}`);
        // Update the existing change with the new data
        updatedChangeTracking[existingChangeIndex] = {
          action,
          originalObj,
          newObj,
          id
        };
      } else {
        //console.log(`Adding new change for ID: ${id}`);
        // Add a new change record
        updatedChangeTracking.push({
          action,
          originalObj,
          newObj,
          id
        });
      }

      //console.log('Updated change tracking:', updatedChangeTracking);

      return {
        changeTracking: updatedChangeTracking,
        hasLocalChanges: true
      };
    }, () => {
      // After updating the change tracking, update the displayed locations
      this.updateDisplayedLocations();

      // Notify parent that this section has unsaved changes
      if (this.props.onSectionHasChanges) {
        this.props.onSectionHasChanges(true);
      }
    });
  }


  handleRequestFilter = (value, property) => {
    //console.log('Filter request:', { property, value });

    // Get the locations we should be filtering on
    const locationsToFilter = this.state.hasLocalChanges ?
      this.getCurrentLocations() :
      this.props.locations;

    //console.log('Current locations before filtering:', locationsToFilter);

    if (value.length < 1) {
      // No filter - show all locations
      //console.log('Clearing filter, showing all locations');
      this.props.setFilter(locationsToFilter);
      return true;
    }

    //console.log(`Filtering on property: ${property} with value: ${value}`);

    // Special handling for FOCUS which needs to look at details array
    if (property === 'FOCUS') {
      const filteredLocations = locationsToFilter.filter(location => {
        // Check if location has details with focus areas
        if (!location.details || !Array.isArray(location.details)) {
          return false;
        }

        // Check if any focus area includes the search term
        return location.details.some(detail => {
          const focusName = detail.focusName || detail.focusShortDesc || '';
          return focusName.toUpperCase().includes(value.toUpperCase());
        });
      });

      //console.log(`Found ${filteredLocations.length} locations matching focus filter`);
      this.setState({ page: 0 });
      this.props.setFilter(filteredLocations);
      return true;
    }

    // Standard property filtering
    const filteredLocations = locationsToFilter.filter(location => {
      // Handle nested properties like 'address'
      let propertyValue = location[property];

      // Skip if property doesn't exist
      if (propertyValue === undefined || propertyValue === null) {
        return false;
      }

      // Convert to string and uppercase for case-insensitive comparison
      const testValue = propertyValue.toString().toUpperCase();
      const searchValue = value.toUpperCase();

      // Check if the property value includes the search term
      const matches = testValue.includes(searchValue);

      if (matches) {
        //console.log(`Match found for ${property}: ${testValue} includes ${searchValue}`);
      }

      return matches;
    });

    //console.log(`Filter results: ${filteredLocations.length} locations match criteria`);
    this.setState({ page: 0 });
    this.props.setFilter(filteredLocations);
    return true;
  }



  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage })
  }

  handleChangeRowsPerPage = event => {
    this.setState({ page: 0, rowsPerPage: event.target.value })
  }

  handleViewDetails(details, officeName) {
    this.setState({ details: details, showDlg: true, filtered: details, officeName: officeName });
  }

  handleAddNewLocation = () => {
    this.setState({ formErrors: {}, validationMessage: '' });
    // Create an empty location object with default values
    const newLocation = {
      poflOfcId: `temp_${Date.now()}`, // Temporary ID until saved
      phoOfficeName: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      county: '',
      zip: '',
      zipExt: '',
      ofcDirectoryPhoneNbr: '',
      ofcFaxNbr: '',
      faxExt: '',
      ofcEmailAddress: '',
      ptcTaxId: this.props.selectedTin || '',
      poflAcceptWcPatientsFlag: '',
      focusAreas: [],
      details: [],
      isNew: true
    };

    //console.log('Adding new location:', newLocation);

    this.setState({
      editLocation: newLocation,
      showEditDlg: true,
      countyOptions: [] // Clear county options for new locations
    });
  }

  handleEditLocation = (location) => {
    this.setState({ formErrors: {}, validationMessage: '' });

    // Get the most up-to-date version of this location from our current locations
    const currentLocations = this.getCurrentLocations();
    const currentLocation = currentLocations.find(loc => loc.poflOfcId === location.poflOfcId);

    if (!currentLocation) {
      console.error(`Could not find location with ID ${location.poflOfcId} in current locations`);
      return;
    }

    // Make a deep copy to avoid reference issues
    const locationToEdit = JSON.parse(JSON.stringify(currentLocation));

    // Parse fax extension if exists
    let faxNumber = locationToEdit.ofcFaxNbr || '';
    let faxExt = '';
    if (faxNumber.includes('ext')) {
      const faxParts = faxNumber.split('ext');
      faxNumber = faxParts[0].trim();
      faxExt = faxParts[1].trim();
    }

    // Parse focus areas from details if available
    let focusAreas = [];
    if (locationToEdit.details && Array.isArray(locationToEdit.details)) {
      focusAreas = locationToEdit.details
        .map(detail => detail.focusShortDesc || detail.focusName)
        .filter(Boolean);
    }

    // Convert Y/N to YES/NO for the accepting patients flag
    // Convert Y/N to YES/NO for the accepting patients flag
    let acceptingWcPatients = locationToEdit.poflAcceptWcPatientsFlag || '';
    if (acceptingWcPatients === 'Y') acceptingWcPatients = 'YES';
    if (acceptingWcPatients === 'N') acceptingWcPatients = 'NO';

    // Store the current value BEFORE clearing it
    const currentAcceptingWcValue = acceptingWcPatients;

    // Check if this location has been edited before by looking at the change tracking
    const hasBeenModified = this.state.changeTracking.some(
      change => change.id === location.poflOfcId &&
        (change.action === 'edit' || change.action === 'add')
    );

    // Determine helper text based on whether it's been modified before
    let wcPatientsHelperText = '';
    if (!currentAcceptingWcValue || currentAcceptingWcValue.trim() === '') {
      wcPatientsHelperText = 'This field is required. Please select Yes or No.';
    } else if (hasBeenModified) {
      // Already modified - show previous value
      wcPatientsHelperText = `Previous value was ${currentAcceptingWcValue}. Please verify your selection.`;
    }

    console.log('Current Accepting WC Value:', currentAcceptingWcValue);
    console.log('Helper Text:', wcPatientsHelperText);

    // Check if we have stored address components from a previous edit
    if (locationToEdit.addressComponents) {
      this.setState({
        editLocation: {
          ...locationToEdit,
          address1: locationToEdit.addressComponents.address1 || '',
          address2: locationToEdit.addressComponents.address2 || '',
          city: locationToEdit.addressComponents.city || '',
          state: locationToEdit.addressComponents.state || '',
          county: locationToEdit.addressComponents.county || '',
          zip: locationToEdit.addressComponents.zip || '',
          zipExt: locationToEdit.addressComponents.zipExt || '',
          ofcFaxNbr: faxNumber,
          faxExt,
          focusAreas: focusAreas,
          poflAcceptWcPatientsFlag: hasBeenModified ? acceptingWcPatients : '',
          currentAcceptingWcValue: currentAcceptingWcValue,
          wcPatientsHelperText: wcPatientsHelperText,
          isNew: locationToEdit.isNew
        },
        showEditDlg: true,
        showAcceptingWcMessage: true
      }, () => {
        // After state is updated, fetch counties if state has a value
        if (locationToEdit.addressComponents.state) {
          this.fetchCountiesForState(locationToEdit.addressComponents.state);
        }
      });
    } else {
      // No stored address components, set up with empty address fields
      this.setState({
        editLocation: {
          ...locationToEdit,
          address1: '',
          address2: '',
          city: '',
          state: '',
          county: '',
          zip: '',
          zipExt: '',
          ofcFaxNbr: faxNumber,
          faxExt,
          focusAreas: focusAreas,
          poflAcceptWcPatientsFlag: hasBeenModified ? acceptingWcPatients : '',
          currentAcceptingWcValue: currentAcceptingWcValue,
          wcPatientsHelperText: wcPatientsHelperText,
          isNew: locationToEdit.isNew
        },
        showEditDlg: true,
        showAcceptingWcMessage: true
      });

      // Fetch address data from server only
      const addressId = locationToEdit.ofcDirectoryAddId;
      if (addressId) {
        ProviderService.getAddressById(addressId)
          .then((response) => {
            if (isNotEmpty(response.data)) {
              const data = response.data;

              const addressComponents = {
                address1: data.addStreet1 || '',
                address2: data.addStreet2 || '',
                city: data.addCity || '',
                state: data.addState || '',
                county: data.addCounty || '',
                zip: data.addZipCode || '',
                zipExt: data.addZipPlusFour || ''
              };

              // Update the location in our working locations with the address components
              const updatedLocations = this.getCurrentLocations().map(loc => {
                if (loc.poflOfcId === locationToEdit.poflOfcId) {
                  return {
                    ...loc,
                    addressComponents: addressComponents
                  };
                }
                return loc;
              });

              // Update state with the address data once it's available
              this.setState(prevState => ({
                editLocation: {
                  ...prevState.editLocation,
                  ...addressComponents
                },
                workingLocations: updatedLocations
              }), () => {
                // After state is updated, fetch counties if state has a value
                if (data.addState) {
                  this.fetchCountiesForState(data.addState);
                }

                // Update parent component with the updated locations that include address components
                if (this.props.updateLocations) {
                  this.props.updateLocations(updatedLocations);
                }
              });
            } else {
              console.log('No address data found');
            }
          })
          .catch((error) => {
            console.log('Error GETTING THE PROVIDERS: ' + error);
          });
      } else {
        console.log('No address ID available, cannot fetch address data');
      }
    }
  }

  // Modified to show confirmation dialog first and fetch address data
  handleConfirmDeleteLocation = (locationId) => {
    console.log(`Confirming deletion for location ${locationId}`);
    const currentLocations = this.getCurrentLocations().filter(
      loc => !this.isLocationDeleted(loc.poflOfcId)
    );

    // Check if this is the only location
    if (currentLocations.length === 1) {
      // Show warning dialog instead of delete confirmation
      this.setState({
        showSingleLocationWarning: true
      });
      return;
    }
    // Find the location to delete
    //const currentLocations = this.getCurrentLocations();
    const locationToDelete = currentLocations.find(loc => loc.poflOfcId === locationId);

    if (!locationToDelete) {
      console.error(`Could not find location with ID ${locationId} to delete`);
      return;
    }

    // Check if this is a newly added location
    const isNewLocation = locationToDelete.isNew ||
      !this.state.originalLocations.some(loc => loc.poflOfcId === locationId);

    // For newly added locations, we already have complete data
    if (isNewLocation) {
      this.setState({
        showDeleteConfirmDialog: true,
        locationToDelete: locationToDelete,
        isNewLocationToDelete: isNewLocation
      });
      return;
    }

    // For existing locations, always fetch address data to ensure we have complete information
    const addressId = locationToDelete.ofcDirectoryAddId;

    // Show loading state if needed
    this.setState({ loadingDeleteAddress: true });

    if (addressId) {
      console.log(`Fetching address data for deletion, ID: ${addressId}`);

      ProviderService.getAddressById(addressId)
        .then((response) => {
          if (isNotEmpty(response.data)) {
            const data = response.data;
            console.log('Address data loaded for deletion:', data);

            // Create address components object
            const addressComponents = {
              address1: data.addStreet1 || '',
              address2: data.addStreet2 || '',
              city: data.addCity || '',
              state: data.addState || '',
              county: data.addCounty || '',
              zip: data.addZipCode || '',
              zipExt: data.addZipPlusFour || ''
            };

            // Update the location with address components
            const updatedLocationToDelete = {
              ...locationToDelete,
              addressComponents: addressComponents
            };

            // Store this updated location in all our working locations to ensure consistency
            const updatedLocations = this.getCurrentLocations().map(loc => {
              if (loc.poflOfcId === locationId) {
                return updatedLocationToDelete;
              }
              return loc;
            });

            // Update state and show confirmation dialog
            this.setState({
              workingLocations: updatedLocations,
              showDeleteConfirmDialog: true,
              locationToDelete: updatedLocationToDelete,
              loadingDeleteAddress: false,
              isNewLocationToDelete: isNewLocation
            }, () => {
              // Update parent component with the updated locations
              if (this.props.updateLocations) {
                this.props.updateLocations(updatedLocations);
              }
            });
          } else {
            console.log('No address data found for deletion');
            // Proceed with deletion anyway, using the location as is
            this.setState({
              showDeleteConfirmDialog: true,
              locationToDelete: locationToDelete,
              loadingDeleteAddress: false,
              isNewLocationToDelete: isNewLocation
            });
          }
        })
        .catch((error) => {
          console.log('Error getting address for deletion: ' + error);
          // Proceed with deletion anyway, using the location as is
          this.setState({
            showDeleteConfirmDialog: true,
            locationToDelete: locationToDelete,
            loadingDeleteAddress: false,
            isNewLocationToDelete: isNewLocation
          });
        });
    } else {
      // No address ID available, proceed with deletion using the location as is
      console.log('No address ID available for deletion, cannot fetch address data');
      this.setState({
        showDeleteConfirmDialog: true,
        locationToDelete: locationToDelete,
        loadingDeleteAddress: false,
        isNewLocationToDelete: isNewLocation
      });
    }
  }
  /*  handleDeleteLocation = () => {
     const locationToDelete = this.state.locationToDelete;
 
     if (!locationToDelete) {
       console.log('No location to delete');
       return;
     }
 
     const locationId = locationToDelete.poflOfcId;
 
     // Check if this is a new location that was just added
     const isNewLocation = locationToDelete.isNew;
 
     if (isNewLocation) {
       // For new locations, remove from change tracking completely
       this.setState(prevState => {
         // Filter out any changes related to this location
         const updatedChangeTracking = prevState.changeTracking.filter(
           change => change.id !== locationId
         );
 
         return {
           changeTracking: updatedChangeTracking,
           showDeleteConfirmDialog: false,
           locationToDelete: null,
           // Only set hasLocalChanges to false if there are no more changes
           hasLocalChanges: updatedChangeTracking.length > 0
         };
       }, () => {
         // Update displayed locations
         this.updateDisplayedLocations();
 
         // Notify parent of changes status
         if (this.props.onSectionHasChanges) {
           this.props.onSectionHasChanges(this.state.changeTracking.length > 0);
         }
       });
     } else {
       // For existing locations, mark for deletion as before
       const originalLocation = this.state.originalLocations.find(
         loc => loc.poflOfcId === locationId
       );
 
       if (!originalLocation) {
         console.error(`Could not find location with ID ${locationId} in original locations`);
         return;
       }
 
       // Track this deletion in the change tracking system
       this.trackChange('delete', originalLocation, null, locationId);
 
       // Close the confirmation dialog
       this.setState({
         showDeleteConfirmDialog: false,
         locationToDelete: null
       });
     }
   } */

  handleDeleteLocation = () => {
    const locationToDelete = this.state.locationToDelete;
    if (!locationToDelete) return;

    const locationId = locationToDelete.poflOfcId;
    const isNewLocation = locationToDelete.isNew;

    // For existing locations, ensure we have address components
    if (!isNewLocation && !locationToDelete.addressComponents && locationToDelete.ofcDirectoryAddId) {
      // Fetch address data first
      ProviderService.getAddressById(locationToDelete.ofcDirectoryAddId)
        .then((response) => {
          if (isNotEmpty(response.data)) {
            const data = response.data;
            const addressComponents = {
              address1: data.addStreet1 || '',
              address2: data.addStreet2 || '',
              city: data.addCity || '',
              state: data.addState || '',
              county: data.addCounty || '',
              zip: data.addZipCode || '',
              zipExt: data.addZipPlusFour || ''
            };

            // Create a complete location object with address components
            const completeLocationToDelete = {
              ...locationToDelete,
              addressComponents: addressComponents
            };

            // Now proceed with deletion using the complete object
            this.trackChange('delete', completeLocationToDelete, null, locationId);
          } else {
            // If no data, proceed with what we have
            this.trackChange('delete', locationToDelete, null, locationId);
          }
        })
        .catch(() => {
          // If fetch fails, proceed with what we have
          this.trackChange('delete', locationToDelete, null, locationId);
        })
        .finally(() => {
          this.setState({
            showDeleteConfirmDialog: false,
            locationToDelete: null
          });
        });
    } else {
      // For new locations or those with address components, proceed as before
      if (isNewLocation) {
        // Handle new location deletion
        this.setState(prevState => {
          const updatedChangeTracking = prevState.changeTracking.filter(
            change => change.id !== locationId
          );
          return {
            changeTracking: updatedChangeTracking,
            showDeleteConfirmDialog: false,
            locationToDelete: null,
            hasLocalChanges: updatedChangeTracking.length > 0
          };
        }, () => {
          this.updateDisplayedLocations();
        });
      } else {
        // Handle existing location deletion
        const originalLocation = this.state.originalLocations.find(
          loc => loc.poflOfcId === locationId
        );

        if (originalLocation) {
          this.trackChange('delete', locationToDelete, null, locationId);
        }

        this.setState({
          showDeleteConfirmDialog: false,
          locationToDelete: null
        });
      }
    }
  }
  // Add method to cancel deletion
  handleCancelDelete = () => {
    this.setState({
      showDeleteConfirmDialog: false,
      locationToDelete: null
    });
  }

  // Update handleUndoDelete to work with the change tracking system
  handleUndoDelete = (locationId) => {
    //console.log(`Undoing deletion for location ${locationId}`);

    // Remove this location from change tracking if it was marked for deletion
    this.setState(prevState => {
      const updatedChangeTracking = prevState.changeTracking.filter(
        change => !(change.id === locationId && change.action === 'delete')
      );

      return {
        changeTracking: updatedChangeTracking,
        // Only set hasLocalChanges to false if there are no more changes
        hasLocalChanges: updatedChangeTracking.length > 0
      };
    }, () => {
      // Update the displayed locations after undoing the deletion
      this.updateDisplayedLocations();

      // Notify parent that this section has changes (or no changes)
      if (this.props.onSectionHasChanges) {
        this.props.onSectionHasChanges(this.state.changeTracking.length > 0);
      }
    });
  }

  // Update isLocationDeleted to check the change tracking array
  isLocationDeleted = (locationId) => {
    if (!locationId) return false;

    return this.state.changeTracking.some(
      change => change.id === locationId && change.action === 'delete'
    );
  }

  isNewAndDeleted = (locationId) => {
    if (!locationId) return false;

    // Check if this location was added and then deleted
    const addChange = this.state.changeTracking.find(
      change => change.id === locationId && change.action === 'add'
    );

    const deleteChange = this.state.changeTracking.find(
      change => change.id === locationId && change.action === 'delete'
    );

    // If it was both added and deleted, it should be removed completely
    return addChange && deleteChange;
  }

  // FIXED: Update the displayed locations and sync with parent
  updateDisplayedLocations = () => {
    //console.log('Updating displayed locations');

    // Get current locations with all changes applied
    let currentLocations = this.getCurrentLocations();
    //console.log('Current locations after applying changes:', currentLocations);

    // Update our working locations
    this.setState({ workingLocations: currentLocations }, () => {
      // Update the parent's filtered data with ALL current locations
      if (this.props.setFilter) {
        //console.log('Updating parent filtered data');
        this.props.setFilter(currentLocations);
      }

      // Also update the parent's main locations data to include new items
      if (this.props.updateLocations) {
        //console.log('Updating parent main locations data');
        this.props.updateLocations(currentLocations);
      }
    });
  }

  formatPhoneNumber = (phoneNumberString) => {
    // Strip all non-digit characters
    let cleaned = ('' + phoneNumberString).replace(/\D/g, '');

    // Limit to 10 digits
    cleaned = cleaned.substring(0, 10);

    // Format as (XXX) XXX-XXXX
    let formatted = cleaned;
    if (cleaned.length > 0) {
      if (cleaned.length <= 3) {
        formatted = `(${cleaned}`;
      } else if (cleaned.length <= 6) {
        formatted = `(${cleaned.substring(0, 3)}) ${cleaned.substring(3)}`;
      } else {
        formatted = `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
      }
    }

    return formatted;
  }

  // Updated method to fetch counties when state changes
  fetchCountiesForState = (stateId) => {
    if (!stateId) {
      this.setState({ countyOptions: [] });
      return;
    }

    this.setState({ loadingCounties: true });

    ProviderService.getCountiesForState(stateId)
      .then(response => {
        if (response && response.data && Array.isArray(response.data)) {
          // Handle the simple array of county names
          const countyOptions = response.data.map(countyName => ({
            value: countyName,
            label: countyName
          }));

          this.setState({
            countyOptions: countyOptions,
            loadingCounties: false
          });
        } else {
          console.error('No county data returned from service or invalid format');
          this.setState({
            countyOptions: [],
            loadingCounties: false
          });
        }
      })
      .catch(error => {
        console.error('Error fetching counties:', error);
        this.setState({
          countyOptions: [],
          loadingCounties: false
        });
      });
  }

  // Handle state field input (auto uppercase) without validation
  handleStateInputChange = (e) => {
    const { name, value } = e.target;

    // Update the state field in the form
    this.setState(prevState => ({
      editLocation: {
        ...prevState.editLocation,
        [name]: value,
        // Clear county when state changes
        county: ''
      }
    }), () => {
      // After state is updated, fetch counties if state has a value
      if (name === 'state' && value) {
        this.fetchCountiesForState(value);
      }
    });
  }

  handleCountyChange = (e) => {
    const { name, value } = e.target;
    this.setState(prevState => ({
      editLocation: {
        ...prevState.editLocation,
        [name]: value
      }
    }));
  }

  // Handle phone number input with formatting but no validation
  handlePhoneInputChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = this.formatPhoneNumber(value);

    this.setState(prevState => ({
      editLocation: {
        ...prevState.editLocation,
        [name]: formattedValue
      }
    }));
  }

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState(prevState => ({
      editLocation: {
        ...prevState.editLocation,
        [name]: value
      }
    }));
  }

  handleMultiSelectChange = (field, newValues) => {
    this.setState(prevState => ({
      editLocation: {
        ...prevState.editLocation,
        [field]: newValues
      }
    }));
  }

  handleToggleFocusExpansion(rowId, event) {
    // Prevent the click from propagating to parent elements
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    var self = this; // Store reference to 'this'
    this.setState(function (prevState) {
      var updatedRows = Object.assign({}, prevState.expandedFocusRows);
      updatedRows[rowId] = !prevState.expandedFocusRows[rowId];

      return {
        expandedFocusRows: updatedRows
      };
    });
  }

  validateLocation = (location) => {
    const errors = {};

    // Office Name validation (required)
    if (!location.phoOfficeName || location.phoOfficeName.trim() === '') {
      errors.phoOfficeName = 'Office name is required';
    }

    // Address validation (required with components)
    if (!location.address1 || location.address1.trim() === '') {
      errors.address1 = 'Street address is required';
    }

    if (!location.city || location.city.trim() === '') {
      errors.city = 'City is required';
    }

    if (!location.state || location.state.trim() === '') {
      errors.state = 'State is required';
    }


    if (!location.zip || location.zip.trim() === '') {
      errors.zip = 'ZIP code is required';
    } else if (!/^\d{5}$/.test(location.zip.trim())) {
      errors.zip = 'ZIP code must be 5 digits';
    }

    // ZIP extension validation (optional but must be valid if provided)
    if (location.zipExt && !/^\d{4}$/.test(location.zipExt.trim())) {
      errors.zipExt = 'ZIP extension must be 4 digits';
    }

    // Phone validation (required, 10 digits)
    if (!location.ofcDirectoryPhoneNbr || location.ofcDirectoryPhoneNbr.trim() === '') {
      errors.ofcDirectoryPhoneNbr = 'Phone number is required';
    } else {
      // Remove any non-digit characters for validation
      const phoneDigits = location.ofcDirectoryPhoneNbr.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        errors.ofcDirectoryPhoneNbr = 'Phone number must be 10 digits';
      }
    }

    // Fax validation (optional but must be valid if provided)
    if (location.ofcFaxNbr && location.ofcFaxNbr.trim() !== '') {
      // Remove any non-digit characters for validation
      const faxDigits = location.ofcFaxNbr.replace(/\D/g, '');
      if (faxDigits.length !== 10) {
        errors.ofcFaxNbr = 'Fax number must be 10 digits';
      }
    }

    // Fax extension validation (optional but must be valid if provided)
    if (location.faxExt && !/^\d+$/.test(location.faxExt.trim())) {
      errors.faxExt = 'Fax extension must contain only digits';
    }

    // Email validation (optional but must be valid if provided)
    if (location.ofcEmailAddress && location.ofcEmailAddress.trim() !== '') {
      // Basic email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(location.ofcEmailAddress.trim())) {
        errors.ofcEmailAddress = 'Please enter a valid email address';
      }
    }
    if (!location.poflAcceptWcPatientsFlag ||
      location.poflAcceptWcPatientsFlag.trim() === '' ||
      !['YES', 'NO'].includes(location.poflAcceptWcPatientsFlag)) {
      // Different error message based on whether it's a new location
      if (location.isNew) {
        errors.poflAcceptWcPatientsFlag = 'Please select Yes or No for accepting new WC patients';
      } else {
        errors.poflAcceptWcPatientsFlag = 'Please select Yes or No';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  handleSaveEdit = () => {
    const location = this.state.editLocation;

    const { isValid, errors } = this.validateLocation(location);

    if (!isValid) {
      this.setState({
        formErrors: errors,
        validationMessage: 'Please fix the validation errors before saving.'
      });
      if (this.dialogContentRef && this.dialogContentRef.current) {
        this.dialogContentRef.current.scrollTop = 0;
      }
      return;
    }

    this.setState({ formErrors: {}, validationMessage: '' });

    let acceptingWcPatients = location.poflAcceptWcPatientsFlag || '';
    if (acceptingWcPatients === 'YES') acceptingWcPatients = 'Y';
    if (acceptingWcPatients === 'NO') acceptingWcPatients = 'N';

    // Get the original location to check if address actually changed
    const originalLoc = this.state.originalLocations.find(
      loc => loc.poflOfcId === location.poflOfcId
    );

    // Check if address components actually changed
    const addressChanged =
      originalLoc &&
      originalLoc.addressComponents &&
      (originalLoc.addressComponents.address1 !== location.address1 ||
        originalLoc.addressComponents.address2 !== location.address2 ||
        originalLoc.addressComponents.city !== location.city ||
        originalLoc.addressComponents.state !== location.state ||
        originalLoc.addressComponents.county !== location.county ||
        originalLoc.addressComponents.zip !== location.zip ||
        originalLoc.addressComponents.zipExt !== location.zipExt);

    // Only reconstruct address if it actually changed, otherwise use original
    let fullAddress = originalLoc && originalLoc.address && !addressChanged
      ? originalLoc.address
      : this.reconstructAddress(location);

    let fullFaxNumber = location.ofcFaxNbr || '';
    if (location.faxExt) {
      fullFaxNumber += ' ext ' + location.faxExt;
    }

    const today = new Date();
    let wcPatientsDate = today;

    const addressComponents = {
      address1: location.address1 || '',
      address2: location.address2 || '',
      city: location.city || '',
      state: location.state || '',
      county: location.county || '',
      zip: location.zip || '',
      zipExt: location.zipExt || ''
    };

    const updatedLocation = {
      ...location,
      address: fullAddress,
      ofcFaxNbr: fullFaxNumber,
      poflAcceptWcPatientsFlag: acceptingWcPatients,
      poflAcceptWcPatientsDate: wcPatientsDate,
      addressComponents: addressComponents
    };

    if (location.focusAreas && Array.isArray(location.focusAreas)) {
      const selectedFocusOptions = location.focusAreas.map(focusDesc =>
        this.state.focusOptions.find(option => option.value === focusDesc)
      ).filter(Boolean);

      updatedLocation.details = selectedFocusOptions.map(focus => ({
        focusId: focus.id,
        focusShortDesc: focus.value,
        focusAcronym: focus.acronym,
        focusName: focus.value,
        poflOfcId: location.poflOfcId
      }));
    }

    delete updatedLocation.address1;
    delete updatedLocation.address2;
    delete updatedLocation.city;
    delete updatedLocation.state;
    delete updatedLocation.county;
    delete updatedLocation.zip;
    delete updatedLocation.zipExt;
    delete updatedLocation.faxExt;
    delete updatedLocation.focusAreas;

    if (originalLoc && !originalLoc.addressComponents) {
      originalLoc.addressComponents = addressComponents;
    }

    if (location.isNew) {
      this.trackChange('add', {}, updatedLocation, updatedLocation.poflOfcId);
    } else {
      this.trackChange('edit', originalLoc || {}, updatedLocation, updatedLocation.poflOfcId);
    }

    this.setState({
      editLocation: null,
      showEditDlg: false
    });
  }

  // Helper method to reconstruct address in original format
  reconstructAddress = (location) => {
    let fullAddress = location.address1 || '';
    if (location.address2) {
      fullAddress += ', ' + location.address2;
    }

    fullAddress += ', \n' + (location.city || '') + ', ' + (location.state || '');

    if (location.zip) {
      fullAddress += ', ' + location.zip;
      if (location.zipExt) {
        fullAddress += ' - ' + location.zipExt;
      }
    }

    if (location.county) {
      fullAddress += ', \n' + location.county;
    }

    return fullAddress;
  }

  getCurrentLocations = () => {
    // Start with original locations
    let currentLocations = JSON.parse(JSON.stringify(this.state.originalLocations || []));

    // Track IDs of locations that were added and then deleted
    const addedThenDeletedIds = this.state.changeTracking
      .filter(change => change.action === 'add')
      .map(change => change.id)
      .filter(id =>
        this.state.changeTracking.some(
          change => change.action === 'delete' && change.id === id
        )
      );

    // Apply all tracked changes
    this.state.changeTracking.forEach(change => {
      // Skip changes for locations that were added and then deleted
      if (addedThenDeletedIds.includes(change.id)) return;

      if (change.action === 'edit') {
        // Apply edits
        const index = currentLocations.findIndex(loc => loc.poflOfcId === change.id);
        if (index !== -1) {
          currentLocations[index] = change.newObj;
        }
      } else if (change.action === 'add') {
        // Add new locations
        const exists = currentLocations.some(loc => loc.poflOfcId === change.id);
        if (!exists) {
          currentLocations.push(change.newObj);
        }
      }
      // We don't remove deleted locations here, they stay in the list with strikethrough
    });

    return currentLocations;
  }

  // Generate changes summary for review
  generateChangesSummary = () => {
    const { changeTracking } = this.state;

    // Track changes
    const summary = {
      newLocations: [],
      modifiedLocations: [],
      deletedLocations: []
    };

    // Process all tracked changes
    changeTracking.forEach(change => {
      if (change.action === 'add') {
        summary.newLocations.push(change.newObj);
      } else if (change.action === 'edit') {
        summary.modifiedLocations.push({
          original: change.originalObj,
          modified: change.newObj
        });
      } else if (change.action === 'delete') {
        summary.deletedLocations.push(change.originalObj);
      }
    });

    return summary;
  }


  // Handle showing changes summary
  handleShowChanges = () => {
    console.log('Showing changes summary');

    // If there are no changes, do nothing
    if (!this.state.hasLocalChanges) {
      console.log('No local changes to review');
      return;
    }

    // Generate a summary of changes for the dialog
    const changesSummary = this.generateChangesSummary();

    console.log('Changes summary:', changesSummary);

    // Show the changes summary dialog
    this.setState({
      changesSummary,
      showChangesSummaryDialog: true
    });


  };

  // Add method to close the changes summary dialog
  handleCloseChangesSummary = () => {
    this.setState({ showChangesSummaryDialog: false });
  }

  handleDiscardChanges = () => {
    //console.log('Discarding all changes');
    //console.log('Current change tracking to discard:', this.state.changeTracking);
    //console.log('Reverting to pristine locations:', this.state.pristineLocations);

    // Reset to pristine data from when the component first loaded
    const pristineLocations = JSON.parse(JSON.stringify(this.state.pristineLocations));

    // Update both working and original locations to pristine state
    this.setState({
      originalLocations: pristineLocations,
      workingLocations: pristineLocations,
      deletedLocations: [],
      changeTracking: [],
      hasLocalChanges: false
    }, () => {
      //console.log('Change tracking after discard (should be empty):', this.state.changeTracking);

      // Update the parent component with pristine data
      if (this.props.setFilter) {
        this.props.setFilter(pristineLocations);
      }

      // Notify parent component about discarded changes
      if (this.props.onSectionDiscardChanges) {
        this.props.onSectionDiscardChanges({
          pristineObject: pristineLocations,
          modifiedObject: null
        });
      }

      // Notify parent that this section no longer has unsaved changes
      if (this.props.onSectionHasChanges) {
        this.props.onSectionHasChanges(false);
      }
    });
  }


  syncWithParent = () => {
    const currentLocations = this.getCurrentLocations();

    // Update parent's filtered locations
    if (this.props.setFilter) {
      //console.log('Syncing filtered locations with parent');
      this.props.setFilter(currentLocations);
    }

    // Update parent's main locations if available
    if (this.props.updateLocations) {
      this.props.updateLocations(currentLocations);
    }
  }

  render() {
    const { classes } = this.props;
    const {
      editLocation,
      showEditDlg,
      locationToDelete,
      showDeleteConfirmDialog
    } = this.state;

    // FIXED: Use the correct data source for display
    // If we have local changes, use our working copy
    // Otherwise use the filtered locations from the parent
    const displayLocations = this.props.filtered || [];


    return (
      <div style={{ width: '100%', maxWidth: '100%' }}>
        {/* Single Location Warning Dialog */}
        <Dialog
          open={this.state.showSingleLocationWarning || false}
          onClose={() => this.setState({ showSingleLocationWarning: false })}
          aria-labelledby="single-location-warning-title"
        >
          <DialogTitle id="single-location-warning-title">
            <Box display="flex" alignItems="center">
              <WarningIcon
                style={{
                  color: '#f57c00', // Orange warning color
                  marginRight: '8px',
                  fontSize: '28px'
                }}
              />
              Cannot Delete Only Location
            </Box>

          </DialogTitle>
          <DialogContent>
            <Typography>
              Requests to terminate your only remaining practice location will not be processed without the addition of a replacement location.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.setState({ showSingleLocationWarning: false })}
              color="primary"
              variant="contained"
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
        {/* View Details Dialog */}
        <Dialog
          open={this.state.showDlg}
          onClose={this.closeMsgDlg}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth
          sx={{
            display: 'flex',
            flexDirection: 'column',
            m: 'auto',
            width: 'fit-content',
          }}
        >
          <DialogTitle id="alert-dialog-title">{`Location Focus for ${this.state.officeName}`}
          </DialogTitle>

          <PhysicianLocationDtlsTbl dtlss={this.state.filtered} setFilter={this.setFilter} filtered={this.state.details} />

          <div style={{ textAlign: 'center' }}>
            <Button variant="outlined" autoFocus onClick={this.closeMsgDlg} aria-label='PROVIDER DEMOGRAPHICS: Physician Focus: View DETAILS CLOSE Button'>
              CLOSE</Button>
          </div>
        </Dialog>

        {/* Edit Location Dialog */}
        <Dialog
          open={showEditDlg}
          onClose={this.closeEditDlg}
          aria-labelledby="edit-dialog-title"
          aria-describedby="edit-dialog-description"
          fullWidth
          maxWidth="md"
        >
          <DialogTitle id="edit-dialog-title">
            {editLocation?.isNew ? "Add New Location" : "Edit Location"}
          </DialogTitle>
          <DialogContent ref={this.dialogContentRef}>
            {this.state.validationMessage && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                border: '1px solid #f5c6cb'
              }}>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {this.state.validationMessage}
                </Typography>
              </div>
            )}
            {editLocation && (
              <Grid container spacing={2}>
                {/* Practice TIN and Office Name fields */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="ptcTaxId"
                    label="Practice TIN"
                    value={editLocation.ptcTaxId || ''}
                    onChange={this.handleInputChange}
                    disabled={true} // Always disabled
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    helperText="Provider TIN"
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    name="phoOfficeName"
                    label="Location Name"
                    value={editLocation.phoOfficeName || ''}
                    onChange={this.handleInputChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!this.state.formErrors?.phoOfficeName}
                    helperText={this.state.formErrors?.phoOfficeName || "Required"}
                    required
                    disabled={!editLocation.isNew}
                  />
                </Grid>

                {/* Address fields broken down */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ marginTop: '10px', marginBottom: '-5px' }}>
                    Address Information
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="address1"
                    label="Address Line 1"
                    value={editLocation.address1 || ''}
                    onChange={this.handleInputChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!this.state.formErrors?.address1}
                    helperText={this.state.formErrors?.address1 || "Required"}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="address2"
                    label="Address Line 2"
                    value={editLocation.address2 || ''}
                    onChange={this.handleInputChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                  />
                </Grid>

                {/* City, State, County fields */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="city"
                    label="City"
                    value={editLocation.city || ''}
                    onChange={this.handleInputChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!this.state.formErrors?.city}
                    helperText={this.state.formErrors?.city || "Required"}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="state"
                    label="State"
                    value={editLocation.state || ''}
                    onChange={this.handleStateInputChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    select
                    SelectProps={{
                      native: true,
                    }}
                    error={!!this.state.formErrors?.state}
                    helperText={this.state.formErrors?.state || "Required"}
                    required
                    disabled={this.state.loadingStates}
                  >
                    <option value="">
                      {this.state.loadingStates
                        ? "Loading states..."
                        : "Select..."}
                    </option>
                    {this.state.stateOptions.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    name="county"
                    label="County"
                    value={editLocation.county || ''}
                    onChange={this.handleCountyChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    select
                    SelectProps={{
                      native: true,
                    }}
                    error={!!this.state.formErrors?.county}
                    helperText={this.state.formErrors?.county || "Optional"}
                    disabled={this.state.loadingCounties || this.state.countyOptions.length === 0}
                  >
                    <option value="">
                      {this.state.loadingCounties
                        ? "Loading counties..."
                        : this.state.countyOptions.length === 0
                          ? "Select state first"
                          : "Select county..."}
                    </option>
                    {this.state.countyOptions.map((county) => (
                      <option key={county.value} value={county.value}>
                        {county.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                {/* ZIP Code and Extension fields */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    name="zip"
                    label="ZIP Code"
                    value={editLocation.zip || ''}
                    onChange={this.handleInputChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!this.state.formErrors?.zip}
                    helperText={this.state.formErrors?.zip || "5 digits"}
                    required
                    inputProps={{ maxLength: 5 }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    name="zipExt"
                    label="ZIP Extension"
                    value={editLocation.zipExt || ''}
                    onChange={this.handleInputChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!this.state.formErrors?.zipExt}
                    helperText={this.state.formErrors?.zipExt || "4 digits (optional)"}
                    inputProps={{ maxLength: 4 }}
                  />
                </Grid>

                {/* Contact information */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ marginTop: '10px', marginBottom: '-5px' }}>
                    Location Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    name="ofcDirectoryPhoneNbr"
                    label="Phone Number"
                    value={editLocation.ofcDirectoryPhoneNbr || ''}
                    onChange={this.handlePhoneInputChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!this.state.formErrors?.ofcDirectoryPhoneNbr}
                    helperText={this.state.formErrors?.ofcDirectoryPhoneNbr || "Required (10 digits)"}
                    required
                    placeholder="(555) 555-5555"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    name="ofcFaxNbr"
                    label="Fax Number"
                    value={editLocation.ofcFaxNbr || ''}
                    onChange={this.handlePhoneInputChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!this.state.formErrors?.ofcFaxNbr}
                    helperText={this.state.formErrors?.ofcFaxNbr || "Optional (10 digits)"}
                    placeholder="(555) 555-5555"
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <TextField
                    name="faxExt"
                    label="Fax Ext"
                    value={editLocation.faxExt || ''}
                    onChange={this.handleInputChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!this.state.formErrors?.faxExt}
                    helperText={this.state.formErrors?.faxExt}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    name="ofcEmailAddress"
                    label="Email Address"
                    value={editLocation.ofcEmailAddress || ''}
                    onChange={this.handleInputChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!this.state.formErrors?.ofcEmailAddress}
                    helperText={this.state.formErrors?.ofcEmailAddress || "Optional"}
                    type="email"
                  />
                </Grid>

                {/* For Existing Locations - Show selection with previous value message */}
                {!editLocation.isNew && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="poflAcceptWcPatientsFlag"
                      label="Accepting New WC?"
                      value={editLocation.poflAcceptWcPatientsFlag || ''}
                      onChange={this.handleInputChange}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      select
                      SelectProps={{
                        native: true,
                      }}
                      error={!!this.state.formErrors?.poflAcceptWcPatientsFlag}
                      helperText={
                        this.state.formErrors?.poflAcceptWcPatientsFlag ||
                        editLocation.wcPatientsHelperText ||
                        `Previous value is ${editLocation.currentAcceptingWcValue || '(Not Set)'}, Please select a value.`
                      }
                      required

                    >
                      <option value="">Select...</option>
                      <option value="YES">Yes</option>
                      <option value="NO">No</option>
                    </TextField>
                  </Grid>
                )}

                {/* For New Locations - Just show the selection field */}
                {editLocation.isNew && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="poflAcceptWcPatientsFlag"
                      label="Accepting New WC?"
                      value={editLocation.poflAcceptWcPatientsFlag || ''}
                      onChange={this.handleInputChange}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      select
                      SelectProps={{
                        native: true,
                      }}
                      error={!!this.state.formErrors?.poflAcceptWcPatientsFlag}
                      helperText={this.state.formErrors?.poflAcceptWcPatientsFlag || "Required"}
                      required
                    >
                      <option value="">Select...</option>
                      <option value="YES">Yes</option>
                      <option value="NO">No</option>
                    </TextField>
                  </Grid>
                )}
                {/* Focus Areas section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ marginTop: '20px', marginBottom: '5px' }}>
                    Focus Areas (Optional)
                  </Typography>
                  <CheckboxSelection
                    title="Select Focus Areas"
                    value={editLocation.focusAreas || []}
                    onChange={this.handleMultiSelectChange}
                    options={this.state.focusOptions}
                    field="focusAreas"
                    sortAlphabetically={true}
                    displayProperty="label"     // Show the formatted label (acronym - desc)
                    valueProperty="value"
                    height="150px"
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeEditDlg} variant="outlined" aria-label="Cancel edit">
              Cancel
            </Button>
            <Button onClick={this.handleSaveEdit} color="primary" variant="contained" aria-label="Save location changes">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog using the reusable component */}
        <ConfirmationDialog
          open={showDeleteConfirmDialog}
          title="Confirm Delete Location"
          message={locationToDelete && !this.state.originalLocations.some(
            loc => loc.poflOfcId === locationToDelete.poflOfcId
          ) ? "Are you sure you want to delete this newly added location?" : "Are you sure you want to delete this location?"}
          detailsComponent={
            locationToDelete && (
              <>
                {!this.state.originalLocations.some(
                  loc => loc.poflOfcId === locationToDelete.poflOfcId
                ) && (
                    <Typography variant="subtitle2" style={{ color: '#f44336', marginBottom: '8px' }}>
                      <strong>This is a newly added location that hasn't been saved to the server.</strong>
                    </Typography>
                  )}
                <Typography variant="subtitle2">
                  <strong>Office Name:</strong> {locationToDelete.phoOfficeName}
                </Typography>
                <Typography variant="subtitle2">
                  <strong>Address:</strong> {locationToDelete.address}
                </Typography>
                <Typography variant="subtitle2">
                  <strong>Phone:</strong> {locationToDelete.ofcDirectoryPhoneNbr}
                </Typography>
              </>
            )
          }
          onConfirm={this.handleDeleteLocation}
          onCancel={this.handleCancelDelete}
          confirmText="Delete"
          confirmColor="secondary"
          severity="error"
        />


        {/* Changes Summary Dialog */}
        {/* Changes Summary Dialog */}
        <Dialog
          open={this.state.showChangesSummaryDialog}
          onClose={this.handleCloseChangesSummary}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Review Location Changes</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {this.state.changesSummary.newLocations.length === 0 &&
              this.state.changesSummary.modifiedLocations.length === 0 &&
              this.state.changesSummary.deletedLocations.length === 0 ? (
              <Typography>No changes to review.</Typography>
            ) : (
              <div>
                {/* New Locations Section */}
                {this.state.changesSummary.newLocations.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#4caf50', marginBottom: '8px' }}>
                      New Locations ({this.state.changesSummary.newLocations.length})
                    </Typography>
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                      <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ fontWeight: 'bold', width: '12%' }}>Name</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '18%' }}>Address</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>Phone</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>Fax</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '12%' }}>Email</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>TIN</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>Accepting WC?</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>WC Date</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '10%' }}>Focus</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {this.state.changesSummary.newLocations.map((location, index) => (
                            <TableRow key={`new-${index}`} style={{ backgroundColor: '#e8f5e9' }}>
                              <TableCell style={{ wordBreak: 'break-word' }}>
                                {location.phoOfficeName}
                              </TableCell>
                              <TableCell style={{ wordBreak: 'break-word' }}>
                                {location.address}
                              </TableCell>
                              <TableCell>
                                {location.ofcDirectoryPhoneNbr}
                              </TableCell>
                              <TableCell>
                                {location.ofcFaxNbr}
                              </TableCell>
                              <TableCell style={{ wordBreak: 'break-word' }}>
                                {location.ofcEmailAddress}
                              </TableCell>
                              <TableCell>
                                {location.ptcTaxId}
                              </TableCell>
                              <TableCell>
                                {formatYesNo(location.poflAcceptWcPatientsFlag)}
                              </TableCell>
                              <TableCell>
                                {formatDateForDisplay(location.poflAcceptWcPatientsDate)}
                              </TableCell>
                              <TableCell style={{ wordBreak: 'break-word' }}>
                                {formatFocusAreasComplete(location.details)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Modified Locations Section */}
                {this.state.changesSummary.modifiedLocations.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#2196f3', marginBottom: '8px' }}>
                      Modified Locations ({this.state.changesSummary.modifiedLocations.length})
                    </Typography>

                    {/* Explanation message */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        backgroundColor: '#e3f2fd',
                        padding: '6px 12px',
                        borderRadius: '4px'
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: '16px',
                          height: '16px',
                          backgroundColor: '#ffeb3b40',
                          border: '1px solid #ffeb3b',
                          marginRight: '8px',
                          borderRadius: '2px'
                        }}
                      ></span>
                      <Typography variant="body2" style={{ fontStyle: 'italic' }}>
                        Highlighted values indicate modified fields
                      </Typography>
                    </Box>

                    <div style={{ width: '100%', overflowX: 'auto' }}>
                      <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ fontWeight: 'bold', width: '12%' }}>Name</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '18%' }}>Address</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>Phone</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>Fax</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '12%' }}>Email</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>TIN</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>Accepting WC?</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>WC Date</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '10%' }}>Focus</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {this.state.changesSummary.modifiedLocations.map((change, index) => {
                            // Helper function to determine if a field has changed
                            const hasChanged = (field) => {
                              // Special handling for address field
                              if (field === 'address') {
                                const origAddress = change.original.address || '';
                                const modAddress = change.modified.address || '';

                                const normalizeAddress = (addr) => {
                                  return addr
                                    .replace(/\n/g, ' ')
                                    .replace(/\s+/g, ' ')
                                    .replace(/\s*,\s*/g, ',')
                                    .replace(/\s*-\s*/g, '-')
                                    .trim()
                                    .toLowerCase();
                                };

                                return normalizeAddress(origAddress) !== normalizeAddress(modAddress);
                              }

                              // Special handling for focusAreas/details which is an array
                              if (field === 'details') {
                                const origFocus = formatFocusAreasComplete(change.original.details || []);
                                const modFocus = formatFocusAreasComplete(change.modified.details || []);
                                return origFocus !== modFocus;
                              }

                              // Special handling for poflAcceptWcPatientsFlag
                              if (field === 'poflAcceptWcPatientsFlag') {
                                return formatYesNo(change.original[field]) !== formatYesNo(change.modified[field]);
                              }

                              // Special handling for poflAcceptWcPatientsDate
                              if (field === 'poflAcceptWcPatientsDate') {
                                const origDate = change.original[field]
                                  ? new Date(change.original[field]).toLocaleDateString('en-US')
                                  : '';
                                const modDate = change.modified[field]
                                  ? new Date(change.modified[field]).toLocaleDateString('en-US')
                                  : '';
                                return origDate !== modDate;
                              }

                              return change.original[field] !== change.modified[field];
                            };

                            // Style for changed values
                            const changedStyle = {
                              backgroundColor: '#ffeb3b40',
                              fontWeight: 'bold',
                              wordBreak: 'break-word'
                            };

                            // Style for unchanged values
                            const unchangedStyle = {
                              wordBreak: 'break-word'
                            };

                            return (
                              <TableRow key={`mod-${index}`} style={{ backgroundColor: '#e3f2fd' }}>
                                <TableCell style={hasChanged('phoOfficeName') ? changedStyle : unchangedStyle}>
                                  {change.modified.phoOfficeName}
                                </TableCell>
                                <TableCell style={hasChanged('address') ? changedStyle : unchangedStyle}>
                                  {change.modified.address}
                                </TableCell>
                                <TableCell style={hasChanged('ofcDirectoryPhoneNbr') ? changedStyle : unchangedStyle}>
                                  {change.modified.ofcDirectoryPhoneNbr}
                                </TableCell>
                                <TableCell style={hasChanged('ofcFaxNbr') ? changedStyle : unchangedStyle}>
                                  {change.modified.ofcFaxNbr}
                                </TableCell>
                                <TableCell style={hasChanged('ofcEmailAddress') ? changedStyle : unchangedStyle}>
                                  {change.modified.ofcEmailAddress}
                                </TableCell>
                                <TableCell style={hasChanged('ptcTaxId') ? changedStyle : unchangedStyle}>
                                  {change.modified.ptcTaxId}
                                </TableCell>
                                <TableCell style={hasChanged('poflAcceptWcPatientsFlag') ? changedStyle : unchangedStyle}>
                                  {formatYesNo(change.modified.poflAcceptWcPatientsFlag)}
                                </TableCell>
                                <TableCell style={hasChanged('poflAcceptWcPatientsDate') ? changedStyle : unchangedStyle}>
                                  {formatDateForDisplay(change.modified.poflAcceptWcPatientsDate)}
                                </TableCell>
                                <TableCell style={hasChanged('details') ? changedStyle : unchangedStyle}>
                                  {formatFocusAreasComplete(change.modified.details)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Deleted Locations Section */}
                {this.state.changesSummary.deletedLocations.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#f44336', marginBottom: '8px' }}>
                      Deleted Locations ({this.state.changesSummary.deletedLocations.length})
                    </Typography>
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                      <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell style={{ fontWeight: 'bold', width: '12%' }}>Name</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '18%' }}>Address</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>Phone</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>Fax</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '12%' }}>Email</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>TIN</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>Accepting WC?</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '8%' }}>WC Date</TableCell>
                            <TableCell style={{ fontWeight: 'bold', width: '10%' }}>Focus</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {this.state.changesSummary.deletedLocations.map((location, index) => (
                            <TableRow key={`del-${index}`} style={{ backgroundColor: '#ffebee' }}>
                              <TableCell style={{ wordBreak: 'break-word' }}>
                                {location.phoOfficeName}
                              </TableCell>
                              <TableCell style={{ wordBreak: 'break-word' }}>
                                {location.address}
                              </TableCell>
                              <TableCell>
                                {location.ofcDirectoryPhoneNbr}
                              </TableCell>
                              <TableCell>
                                {location.ofcFaxNbr}
                              </TableCell>
                              <TableCell style={{ wordBreak: 'break-word' }}>
                                {location.ofcEmailAddress}
                              </TableCell>
                              <TableCell>
                                {location.ptcTaxId}
                              </TableCell>
                              <TableCell>
                                {formatYesNo(location.poflAcceptWcPatientsFlag)}
                              </TableCell>
                              <TableCell>
                                {formatDateForDisplay(location.poflAcceptWcPatientsDate)}
                              </TableCell>
                              <TableCell style={{ wordBreak: 'break-word' }}>
                                {formatFocusAreasComplete(location.details)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseChangesSummary} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <div className={classes.root} style={{ width: '100%', maxWidth: '100%' }}>
          <Paper className={classes.paper} style={{ width: '100%' }}>
            {/* Combined row for Add Location button and pagination */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 16px',
              width: '100%'
            }}>
              {/* Left side - Add Location button */}
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.handleAddNewLocation}
                  startIcon={<AddIcon />}
                  aria-label="Add new location"
                >
                  Add Location
                </Button>
              </div>

              {/* Right side - Pagination */}
              <div>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
                  component="div"
                  count={displayLocations.length}
                  rowsPerPage={this.state.rowsPerPage}
                  page={this.state.page}
                  ActionsComponent={TablePaginationActions}
                  onChangePage={this.handleChangePage}
                  onChangeRowsPerPage={this.handleChangeRowsPerPage}
                  style={{ marginRight: '-16px' }} // Adjust to align with table edge
                />
              </div>
            </div>

            <div className={classes.tableWrapper} style={{ width: '100%', maxWidth: '100%' }}>
              <Table
                className={classes.table}
                size='small'
                aria-label="Physician Location table"
                style={{ width: '100%' }}
              >
                <EnhancedTableHead
                  classes={classes}
                  numSelected={0}
                  order={this.state.order}
                  orderBy={this.state.orderBy}
                  onSelectAllClick={() => { }}
                  onRequestSort={this.handleRequestSort}
                  onRequestFilter={this.handleRequestFilter}
                  rowCount={displayLocations.length}
                />
                <TableBody className="Demographic">
                  {stableSort(displayLocations, getSorting(this.state.order, this.state.orderBy))
                    .filter(row => {
                      // Filter out locations that were added and then deleted
                      const addChange = this.state.changeTracking.find(
                        change => change.id === row.poflOfcId && change.action === 'add'
                      );

                      const deleteChange = this.state.changeTracking.find(
                        change => change.id === row.poflOfcId && change.action === 'delete'
                      );

                      // If it was both added and deleted, filter it out completely
                      return !(addChange && deleteChange);
                    })
                    .slice(this.state.page * this.state.rowsPerPage, this.state.page * this.state.rowsPerPage + this.state.rowsPerPage)
                    .map((row, index) => {
                      const labelId = `enhanced-table-Select-${index}`;
                      const isDeleted = this.isLocationDeleted(row.poflOfcId);
                      return (
                        <TableRow
                          tabIndex={-1}
                          key={row.poflOfcId}
                          style={{
                            padding: '5px 20px',
                            height: 25,
                            backgroundColor: index % 2 ? '#fafafa' : 'white',
                            opacity: isDeleted ? 0.7 : 1
                          }}
                        >
                          <TableCell component="th" id={labelId} scope="row" size='small'
                            className={isDeleted ? classes.deleted : ''}
                            style={{ width: '15%' }}
                          >
                            {/*  {(row.poflPrimaryOfficeFlag === "Y") ?
                              <Tooltip arrow title="Primary" aria-label="Primary Service">
                                <div className="Primary-indicator">
                                  <Avatar sx={{ fontSize: '.5rem' }}>P</Avatar>
                                </div>
                              </Tooltip> : null} */}
                            {row.phoOfficeName}
                          </TableCell>
                          <TableCell component="th" id={labelId} scope="row" size='small'
                            className={isDeleted ? classes.deleted : ''}
                            style={{ width: '20%' }}
                          >
                            <Tooltip title={row.address} placement="top">
                              <div>{row.address}</div>
                            </Tooltip>
                          </TableCell>
                          <TableCell component="th" id={labelId} scope="row" size='small'
                            className={isDeleted ? classes.deleted : ''}
                            style={{ width: '10%' }}
                          >
                            {row.ofcDirectoryPhoneNbr}
                          </TableCell>
                          <TableCell component="th" id={labelId} scope="row" size='small'
                            className={isDeleted ? classes.deleted : ''}
                            style={{ width: '10%' }}
                          >
                            {row.ofcFaxNbr}
                          </TableCell>
                          <TableCell component="th" id={labelId} scope="row" size='small'
                            className={isDeleted ? classes.deleted : ''}
                            style={{ width: '15%' }}
                          >
                            {row.ofcEmailAddress}
                          </TableCell>
                          <TableCell component="th" id={labelId} scope="row" size='small'
                            className={isDeleted ? classes.deleted : ''}
                            style={{ width: '10%' }}
                          >
                            {row.ptcTaxId}
                          </TableCell>
                          <TableCell component="th" id={labelId} scope="row" size='small'
                            className={isDeleted ? classes.deleted : ''}
                            style={{ width: '10%' }}
                          >
                            {formatYesNo(row.poflAcceptWcPatientsFlag)}
                          </TableCell>
                          <TableCell
                            component="th"
                            id={labelId}
                            scope="row"
                            size='small'
                            className={isDeleted ? classes.deleted : ''}
                            style={{ width: '10%' }}
                          >
                            {row.details && row.details.length > 2 ? (
                              <div>
                                {this.state.expandedFocusRows[row.poflOfcId] ? (
                                  <React.Fragment>
                                    {formatFocusAreasComplete(row.details)}
                                    <br />
                                    <a
                                      href="#"
                                      onClick={function (e) { this.handleToggleFocusExpansion(row.poflOfcId, e); }.bind(this)}
                                      style={{ color: '#2196f3', fontSize: '0.8rem', textDecoration: 'none' }}
                                    >
                                      less
                                    </a>
                                  </React.Fragment>
                                ) : (
                                  <React.Fragment>
                                    {formatFocusAreas(row.details)}
                                    <a
                                      href="#"
                                      onClick={function (e) { this.handleToggleFocusExpansion(row.poflOfcId, e); }.bind(this)}
                                      style={{ color: '#2196f3', fontSize: '0.8rem', textDecoration: 'none', marginLeft: '4px' }}
                                    >
                                      more
                                    </a>
                                  </React.Fragment>
                                )}
                              </div>
                            ) : (
                              <div>{formatFocusAreas(row.details)}</div>
                            )}
                          </TableCell>
                          <TableCell component="th" id={labelId} scope="row" size='small' style={{ width: '10%' }}>
                            {isDeleted ? (
                              <Tooltip title="Restore">
                                <IconButton
                                  size="small"
                                  onClick={() => this.handleUndoDelete(row.poflOfcId)}
                                  aria-label="restore location"
                                  color="primary"
                                >
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip title="Edit Location">
                                  <IconButton
                                    aria-label="edit location"
                                    onClick={() => this.handleEditLocation(row)}
                                    size="small"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Location">
                                  <IconButton
                                    aria-label="delete location"
                                    onClick={() => this.handleConfirmDeleteLocation(row.poflOfcId)}
                                    size="small"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 16px',
              width: '100%'
            }}>
              {/* Left side - empty to match layout */}
              <div>
                {/* This space intentionally left empty to match the layout of the top bar */}
              </div>

              {/* Right side - Pagination */}
              <div>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
                  component="div"
                  count={displayLocations.length}
                  rowsPerPage={this.state.rowsPerPage}
                  page={this.state.page}
                  ActionsComponent={TablePaginationActions}
                  onChangePage={this.handleChangePage}
                  onChangeRowsPerPage={this.handleChangeRowsPerPage}
                  style={{ marginRight: '-16px' }} // Adjust to align with table edge
                />
              </div>
            </div>

            <div className={classes.buttonContainer}>
              <Button
                variant="outlined"
                onClick={this.handleDiscardChanges}
                startIcon={<UndoIcon />}
                aria-label="Discard all changes"
                className={classes.actionButton}
                disabled={!this.state.hasLocalChanges}
              >
                Discard Changes
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={this.handleShowChanges}
                startIcon={<VisibilityIcon />}
                aria-label="Review changes"
                className={classes.actionButton}
                disabled={!this.state.hasLocalChanges}
              >
                Review Changes
              </Button>
            </div>
          </Paper>
        </div>
      </div>
    );
  }
}

PhysicianLocationEdit.propTypes = {
  classes: PropTypes.object.isRequired,
  locations: PropTypes.array.isRequired,
  setFilter: PropTypes.func.isRequired,
  filtered: PropTypes.array.isRequired,
  updateLocations: PropTypes.func,
  forwardedRef: PropTypes.object,
  onSectionSaveLocal: PropTypes.func,
  onSectionDiscardChanges: PropTypes.func,
  onSectionHasChanges: PropTypes.func
};

// Create a wrapper component that uses forwardRef
const ForwardedPhysicianLocationEdit = React.forwardRef((props, ref) => {
  return <PhysicianLocationEdit {...props} forwardedRef={ref} />;
});

export default withStyles(useStyles)(ForwardedPhysicianLocationEdit);